import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { AccessPermission, IAccessPermission } from "../../common/models/AccessPermission";
import { hasPermission } from "../../common/utilities/HasPermission";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ConfirmationDialogInitValues } from "../confirmation-dialog/ConfirmationDialog";
import i18n from "../../services/localization-service/i18n";
import { PageConfigurationAccessEnum, PageConfigurationDataModel3 } from "../../services/page-configuration-service/Models";


export enum TabEnum {
    None = -1,
    Devices = 0,
    Wells = 1,
    TankBatteries = 2,
    AlarmLynx = 3,
    CompressorLynx = 4
};


const writePermission = new AccessPermission({
    functionalityType: AccessFunctionalityTypeEnum.PageConfiguration,
    access: AccessTypeEnum.Write_Change
});

const SummaryListingViewUtils = {


    getActionItems(sourceActions: ActionMenuItem[],
        isPhone: boolean,
        offline: boolean,
        configurationID: number,
        configurationData: PageConfigurationDataModel3 | undefined
    ) {
    
        if (offline || !configurationData) {
            return [];
        }
    
        const configurations = (configurationData.privateConfigurations ?? []).concat((configurationData.sharedConfigurations ?? []));
        const hasConfigurations = configurations.length > 0;
    
        const configuration = configurations.find(o => o.id === configurationID);
    
        const userHasWriteAccessToConfiguration = (configuration?.access === PageConfigurationAccessEnum.Private) ||
            configuration?.userHasPermission(writePermission);
    
        const userHasModifyPermission = hasPermission(configurationData.userPermissions as IAccessPermission[], writePermission);
    
        let actions = [...sourceActions];
        let canEditConfiguration = false;
        let canDuplicateConfiguration = false;
        let canModifyConfigurationPermissions = false;
        let canDeleteConfiguration = false;
        let canRenameConfiguration = false;
        let canModify = hasConfigurations;
    
        switch (configuration?.access) {
    
            case PageConfigurationAccessEnum.Private:
                canEditConfiguration = canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = canModify;
                canDeleteConfiguration = canModify;
                canRenameConfiguration = canModify;
                break;
    
            case PageConfigurationAccessEnum.Shared:
                canEditConfiguration = userHasModifyPermission && canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = userHasModifyPermission && canModify;
                canDeleteConfiguration = userHasModifyPermission && canModify;
                canRenameConfiguration = userHasModifyPermission && canModify;
                break;
    
            case PageConfigurationAccessEnum.SharedReadOnly:
                canEditConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDeleteConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canRenameConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                break;
        }
    
        const configurationSelected = (configuration?.id ?? -1) > -1;
    
        // fist determine which actions are visible
        let visibleActions = actions.filter(action => {
            switch (action.action) {
    
                case BaseActionEnum.Export:
                    return configurationSelected;
    
                case BaseActionEnum.Edit:
                    return !isPhone && configurationSelected && canEditConfiguration;
    
                case BaseActionEnum.CreateNew:
                    return !isPhone && !configurationSelected;
    
                case BaseActionEnum.Configure:
                    return !isPhone && configurationSelected;
    
                default:
                    return true;
            }
        });
    
        // next determine the state of the visible actions
        return visibleActions
            .map(action => {
                const actionCopy = { ...action };
    
                switch (action.action) {
    
                    case BaseActionEnum.Configure:
                        actionCopy.subActions = action.subActions?.map(subAction => {
                            const copy = { ...subAction };
    
                            switch (subAction.action) {
    
                                case BaseActionEnum.Edit:
                                    copy.disabled = !canEditConfiguration;
                                    break;
    
                                case BaseActionEnum.CreateNew:
                                    // should always be enabled
                                    copy.disabled = false;
                                    break;
    
                                case BaseActionEnum.Duplicate:
                                    copy.disabled = !canDuplicateConfiguration;
                                    break;
    
                                case BaseActionEnum.ModifyPermissions:
                                    copy.disabled = !canModifyConfigurationPermissions;
                                    break;
    
                                case BaseActionEnum.Delete:
                                    copy.disabled = !canDeleteConfiguration;
                                    break;
    
                                case BaseActionEnum.Rename:
                                    copy.disabled = !canRenameConfiguration;
                                    break;
    
                                default:
                                    copy.disabled = !canModify;
                            }
    
                            copy.icon = undefined;
                            return copy;
                        });
                }
    
                return actionCopy;
            });
    },

    getActionItemsV2(sourceActions: ActionMenuItem[],
        isPhone: boolean,
        offline: boolean,
        configurationID: number,
        configurationData: PageConfigurationDataModel3 | undefined
    ) {
    
        if (offline || !configurationData) {
            return [];
        }
    
        const configurations = (configurationData.privateConfigurations ?? []).concat((configurationData.sharedConfigurations ?? []));
        const hasConfigurations = configurations.length > 0;
    
        const configuration = configurations.find(o => o.id === configurationID);
    
        const userHasWriteAccessToConfiguration = (configuration?.access === PageConfigurationAccessEnum.Private) ||
            configuration?.userHasPermission(writePermission);
    
        const userHasModifyPermission = hasPermission(configurationData.userPermissions as IAccessPermission[], writePermission);
    
        const actions = [...sourceActions];
        let canEditConfiguration = false;
        let canDuplicateConfiguration = false;
        let canModifyConfigurationPermissions = false;
        let canDeleteConfiguration = false;
        let canRenameConfiguration = false;
        const canModify = hasConfigurations;
    
        switch (configuration?.access) {
    
            case PageConfigurationAccessEnum.Private:
                canEditConfiguration = canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = canModify;
                canDeleteConfiguration = canModify;
                canRenameConfiguration = canModify;
                break;
    
            case PageConfigurationAccessEnum.Shared:
                canEditConfiguration = userHasModifyPermission && canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = userHasModifyPermission && canModify;
                canDeleteConfiguration = userHasModifyPermission && canModify;
                canRenameConfiguration = userHasModifyPermission && canModify;
                break;
    
            case PageConfigurationAccessEnum.SharedReadOnly:
                canEditConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDuplicateConfiguration = canModify;
                canModifyConfigurationPermissions = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canDeleteConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                canRenameConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
                break;
        }
    
        const configurationSelected = (configuration?.id ?? -1) > -1;
    
        // fist determine which actions are visible
        const visibleActions = actions.filter(action => {
            switch (action.action) {
    
                case BaseActionEnum.Export:
                    return configurationSelected;
    
                case BaseActionEnum.Edit:
                    return !isPhone && configurationSelected && canEditConfiguration;
    
                case BaseActionEnum.CreateNew:
                    return !isPhone && !configurationSelected;
    
                case BaseActionEnum.Configure:
                    return !isPhone && configurationSelected;
    
                default:
                    return true;
            }
        });
    
        // next determine the state of the visible actions
        return visibleActions
            .map(action => {
                const actionCopy = { ...action };
    
                switch (action.action) {
    
                    case BaseActionEnum.Configure:
                        actionCopy.subActions = action.subActions?.map(subAction => {
                            const copy = { ...subAction };
    
                            switch (subAction.action) {
    
                                case BaseActionEnum.Edit:
                                    copy.disabled = !canEditConfiguration;
                                    break;
    
                                case BaseActionEnum.CreateNew:
                                    // should always be enabled
                                    copy.disabled = false;
                                    break;
    
                                case BaseActionEnum.Duplicate:
                                    copy.disabled = !canDuplicateConfiguration;
                                    break;
    
                                case BaseActionEnum.ModifyPermissions:
                                    copy.disabled = !canModifyConfigurationPermissions;
                                    break;
    
                                case BaseActionEnum.Delete:
                                    copy.disabled = !canDeleteConfiguration;
                                    break;
    
                                case BaseActionEnum.Rename:
                                    copy.disabled = !canRenameConfiguration;
                                    break;
    
                                default:
                                    copy.disabled = !canModify;
                            }
    
                            //copy.icon = undefined;
                            return copy;
                        });
                }
    
                return actionCopy;
            });
    },    
};

export default SummaryListingViewUtils;




const RENAME_CONFIGURATION_DIALOG_PARAMS: ConfirmationDialogInitValues = {
    dialogTitle: i18n.t('configuration.renameConfirmation.title'),
    confirmationText: i18n.t('configuration.renameConfirmation.text'),
    yesButtonText: i18n.t("configuration.renameConfirmation.yesButtonText"),
    noButtonText: i18n.t("configuration.renameConfirmation.noButtonText"),
    showConfirmationText: true,
    showYesButton: true,
    showNoButton: true
};


const ACCESS_LEVEL_MODIFY_CONFIRMATION_PARAMS: ConfirmationDialogInitValues = {
    dialogTitle: i18n.t("configuration.accessLevelModifyConfirmation.title"),
    confirmationText: i18n.t("configuration.accessLevelModifyConfirmation.text"),
    yesButtonText: i18n.t("configuration.accessLevelModifyConfirmation.yesButtonText"),
    noButtonText: i18n.t("configuration.accessLevelModifyConfirmation.noButtonText"),
    showConfirmationText: true,
    showYesButton: true,
    showNoButton: true
};

export {
    RENAME_CONFIGURATION_DIALOG_PARAMS,
    //DELETE_CONFIGURATION_DIALOG_PARAMS,
    ACCESS_LEVEL_MODIFY_CONFIRMATION_PARAMS,
    writePermission
};

